import React, {
  FC, useMemo, useState, useEffect,
} from 'react';
import _ from 'lodash';
import { DropdownItem, Badge, Progress } from 'reactstrap';
import Isvg from 'react-inlinesvg';
import classnames from 'classnames';


import { unformatAmount, getTimeRange, parseAmount } from 'app/utils/parsers';

import StatusBadge from 'app/components/general/StatusBadge';

import { IPaymentRequest } from 'app/ducks/matchPayments/types';
import { getPaymentRequestSummary } from 'app/ducks/paymentRequests/selectors';
import history from 'app/routes/history';
import IconModal from 'app/components/general/IconModal';
import Table from 'app/components/general/TableNew';
import ModalConfirm from 'app/components/general/ModalConfirm';
import Button from 'app/components/general/Button';
import ActionDots from 'app/components/general/ActionDots';
import PlusIcon from 'app/images/icons/plus.svg';
import DeleteIcon from 'app/images/icons/delete.svg';
import EditIcon from 'app/images/icons/edit.svg';


import moment from 'moment';

import { getGroupSummary } from 'app/ducks/groups/selectors';
import { IGroup } from 'app/ducks/groups/types';
import CreateTeam from 'app/components/teams/CreateTeam';

// import ConnectIcon from 'app/images/icons/linked.svg';

interface IProps {
  actions: any;
  paymentRequestsById: IGroup[];
  paymentRequestIds: string[];
  teamId?: string;
  headerTitle?: string;
  appReady?: boolean;
  isLoading?: boolean;
  manual?: boolean;
  saveFilters?: boolean;
  initialFilterConfig?: any;
  persistentFilterConfig?: any;
  pagination?: any;
  selectTeam: (value) => void;
}

const PaymentRequestsTable: FC<IProps> = (props: IProps = {
  manual: true,
  actions: undefined,
  paymentRequestsById: undefined,
  paymentRequestIds: undefined,
  initialFilterConfig: undefined,
  persistentFilterConfig: undefined,
  isLoading: false,
  pagination: undefined,
  selectTeam: undefined,
}) => {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [teamId, setTeamId] = useState(undefined);

  const filterOptions = [
    {
      label: 'Status',
      accessor: 'status',
      type: 'select',
      options: [
        { id: 'pending', name: 'Collecting' },
        { id: 'collected', name: 'Collected' },
      ],
    },
    {
      label: 'Description',
      accessor: 'description',
      type: 'text',
    },
    {
      label: 'Date Due',
      accessor: 'dueAt',
      type: 'datetime',
    },
    {
      label: 'Date Requested',
      accessor: 'createdAt',
      type: 'datetime',
    },
  ];

  const fetchDataFromApi = ({
    sort, filter, pageNumber, pageSize,
  }): void => {
    if (
      props.appReady
      && !props.isLoading
      && (pageNumber !== props.pagination.page.number
        || (!_.isEmpty(sort) && sort !== props.pagination.sort)
        || filter !== props.pagination.filter)
    ) {
      const dueAt = _.find(filter, (f) => f.id === 'dueAt');
      if (dueAt) {
        const { sinceTimeParsed, untilTimeParsed } = getTimeRange(dueAt.value[0], dueAt.value[1]);
        _.remove(filter, (f: any) => f.id === 'dueAt');
        filter.push({ id: 'dueAt.since', value: sinceTimeParsed });
        filter.push({ id: 'dueAt.until', value: untilTimeParsed });
      }

      const createdAt = _.find(filter, (f) => f.id === 'createdAt');
      if (createdAt) {
        const { sinceTimeParsed, untilTimeParsed } = getTimeRange(createdAt.value[0], createdAt.value[1]);
        _.remove(filter, (f: any) => f.id === 'createdAt');
        filter.push({ id: 'createdAt.since', value: sinceTimeParsed });
        filter.push({ id: 'createdAt.until', value: untilTimeParsed });
      }

      props.actions.getAllPaymentRequests({
        page: {
          size: pageSize,
          number: pageNumber,
        },
        sort,
        filter,
      });
    }
    // const payload = {
    //   page: {
    //     size: pageSize,
    //     number: pageNumber,
    //   },
    //   sort,
    //   filter,
    // };
    // props.actions.getAllGroups(payload);
  };

  const handleOpenModal = (e, selectedTeamId): void => {
    e.preventDefault();
    setDeleteModalOpen(true);
    setTeamId(selectedTeamId);
  };

  const handeCloseModal = (e): void => {
    e.preventDefault();
    setDeleteModalOpen(false);
    setTeamId(undefined);
  };

  const handleOpenEditModal = (e, selectedTeamId): void => {
    e.preventDefault();
    props.actions.selectTeam(selectedTeamId);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = (e): void => {
    e.preventDefault();
    setEditModalOpen(false);
  };

  const handeCloseCreateModal = (e): void => {
    e.preventDefault();
    setCreateModalOpen(false);
  };

  const handleDelete = (e): void => {
    e.preventDefault();
    props.actions.deleteTeam({ id: teamId });
  };

  const renderDeleteModal = (): React.ReactNode => (
    <ModalConfirm
      title="Delete Team Member"
      icon={DeleteIcon}
      description="Are you sure you want to delete this team member?"
      buttonText="Delete"
      isOpen={deleteModalOpen}
      isLoading={props.isLoading}
      handleSubmit={(e) => handleDelete(e)}
      handleCancel={handeCloseModal}
    />
  );

  const handleRequestPayment = (): void => {
    history.push('/payments/segments/requests/new');
  };

  const handleClick = (id: string): void => {
    props.selectPaymentRequest(id);
    history.push(`/payments/segments/requests/detail/${id}`);
  };

  const data = useMemo(() => {
    if (_.isEmpty(props.paymentRequestsById) || props.paymentRequestIds === undefined) {
      return [];
    }
    const rows = _.reduce(
      props.paymentRequestIds,
      (obj, id) => {
        if (!_.isEmpty(props.paymentRequestsById[id])) {
          obj.push(getPaymentRequestSummary(props.paymentRequestsById[id]));
        }
        return obj;
      },
      [],
    );
    return rows;
  }, [props.paymentRequestsById, props.paymentRequestIds]);

  const renderDescription = (cellProps: {
    value: {
      description: string;
      numberOfPeople: number;
    };
  }): React.ReactNode => {
    const proNoun = cellProps.value.numberOfPeople > 1 ? 'people' : 'person';
    return (
      <span className="d-inline">
        {cellProps.value.description}
        <span className="margin-left-5 text-weight-medium-bold">
          {`(${cellProps.value.numberOfPeople.toString()} ${proNoun})`}
        </span>
      </span>
    );
  };


  const renderProgress = (cellProps: {
    value: {
      collected: number;
      total: number;
    };
  }): React.ReactNode => {
    const progressPercentage = cellProps.value.collected === 0
      ? 0
      : (cellProps.value.collected / cellProps.value.total) * 100;

    return (
      <div className="d-flex align-items-center">
        <div>
          <Progress multi className="progress-in-table with-margin-right-5">
            <Progress bar color="green" value={progressPercentage} />
            <Progress bar color="grey" value={100 - progressPercentage} />
          </Progress>
        </div>
        <div>
          <span className="d-inline text-weight-medium-bold">{parseAmount(cellProps.value.collected)}</span>
          {' '}
          of
          {' '}
          {parseAmount(cellProps.value.total)}
        </div>
      </div>
    );
  };


  const extraSmallColumn = 'col col12 col-lg-1';
  const smallColumn = 'col col-2 col-lg-2';
  const growColumn = 'col d-flex';
  const largeColumn = 'col col-4 col-md-4 col-lg-4 col-xl-3';
  const flexColumn = 'col col-2 d-none d-md-flex col-xl-1';
  const mediumColumn = 'col col-6 col-md-4 col-lg-4 col-xl-3';


  const columns = useMemo(() => [
    {
      Header: 'Date Requested',
      accessor: 'createdAt',
      className: smallColumn,
      headerClassName: smallColumn,
      Cell: (cellProps) => moment(cellProps.value).format('D MMM YYYY'),
    },
    {
      Header: 'Description',
      accessor: 'summary',
      id: 'description',
      className: growColumn,
      headerClassName: growColumn,
      Cell: renderDescription,
      sortType: (rowA, rowB, id, desc): number => {
        if (rowA.original.summary.description > rowB.original.summary.description) return -1;
        if (rowB.original.summary.description > rowA.original.summary.description) return 1;
        return 0;
      },
    },
    {
      id: 'status',
      Header: 'Status',
      className: smallColumn,
      headerClassName: smallColumn,
      accessor: 'status',
      Cell: (cellProps) => (
        <StatusBadge
          paymentText={cellProps.value}
          formattedPaymentText={cellProps.value === 'pending' ? 'Collecting' : false}
        />
      ),
    },
    {
      id: 'collectionProgress',
      Header: 'Collected',
      className: `${largeColumn} d-none d-lg-flex`,
      headerClassName: `${largeColumn} d-none d-lg-flex`,
      disableSortBy: true,
      accessor: 'collected',
      Cell: renderProgress,
      sortType: (rowA, rowB, id, desc): number => {
        const percentageA = rowA.original.collected.collected / rowA.original.collected.total;
        const percentageB = rowB.original.collected.collected / rowB.original.collected.total;
        if (percentageA > percentageB) return -1;
        if (percentageB > percentageA) return 1;
        return 0;
      },
    },
  ], []);

  const counterText = props.pagination.meta.totalCount === 1 ? '1 payment request' : `${props.pagination.meta.totalCount || 0} payment requests`;

  return (
    <div>
      {renderDeleteModal()}
      <IconModal
        title="Create Team"
        icon={PlusIcon}
        handleCancel={handeCloseCreateModal}
        isOpen={createModalOpen}
      >
        <CreateTeam {...props} teamId={props.teamId} afterSubmit={handeCloseCreateModal} />
      </IconModal>
      <Table
        data={data}
        columns={columns}
        isLoading={props.isLoading}
        // aggressiveLoader
        headerTitle={props.headerTitle}
        objectTitle="payment requests"
        filterTitle="Payment Requests Data"
        counterText={counterText}
        handleClick={handleClick}
        filterable
        saveFilters={props.saveFilters}
        manual={props.manual}
        fetchDataFromApi={fetchDataFromApi}
        filterOptions={filterOptions}
        filterConfig={props.initialFilterConfig}
        persistentFilterConfig={props.persistentFilterConfig}
        pagination={props.pagination}
        showPagination
      >
        <Button onClick={handleRequestPayment} noLoading className="btn-thinner btn-2">
          <Isvg src={PlusIcon} className="btn-icon" />
          New Payment Request
        </Button>
      </Table>
    </div>
  );
};

export default PaymentRequestsTable;
